import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2 } from 'lucide-react';

// Medication Form Dialog
export const MedicationFormDialog = ({ open, onOpenChange, medication, onSave, loading }) => {
  const [form, setForm] = useState({
    medication_name: '',
    dose: '',
    frequency: '',
    route: '',
    prescribing_doctor: '',
    start_date: '',
    end_date: '',
    instructions: ''
  });

  useEffect(() => {
    if (open) {
      setForm({
        medication_name: medication?.medication_name || '',
        dose: medication?.dose || '',
        frequency: medication?.frequency || '',
        route: medication?.route || '',
        prescribing_doctor: medication?.prescribing_doctor || '',
        start_date: medication?.start_date || '',
        end_date: medication?.end_date || '',
        instructions: medication?.instructions || ''
      });
    }
  }, [medication, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{medication ? 'Edit Medication' : 'Add Medication'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="medication_name">Medication Name *</Label>
            <Input
              id="medication_name"
              value={form.medication_name}
              onChange={(e) => setForm({ ...form, medication_name: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dose">Dose</Label>
              <Input
                id="dose"
                value={form.dose}
                onChange={(e) => setForm({ ...form, dose: e.target.value })}
                placeholder="e.g., 10mg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="frequency">Frequency</Label>
              <Input
                id="frequency"
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                placeholder="e.g., Twice daily"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="route">Route</Label>
            <Select value={form.route || 'not_selected'} onValueChange={(v) => setForm({ ...form, route: v === 'not_selected' ? '' : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select route" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_selected">Select route</SelectItem>
                <SelectItem value="oral">Oral</SelectItem>
                <SelectItem value="topical">Topical</SelectItem>
                <SelectItem value="injection">Injection</SelectItem>
                <SelectItem value="inhaled">Inhaled</SelectItem>
                <SelectItem value="sublingual">Sublingual</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prescribing_doctor">Prescribing Doctor</Label>
            <Input
              id="prescribing_doctor"
              value={form.prescribing_doctor}
              onChange={(e) => setForm({ ...form, prescribing_doctor: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="instructions">Instructions/Notes</Label>
            <Textarea
              id="instructions"
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {medication ? 'Update' : 'Add'} Medication
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Allergy Form Dialog
export const AllergyFormDialog = ({ open, onOpenChange, allergy, onSave, loading }) => {
  const [form, setForm] = useState({
    allergy_name: '',
    severity: 'Mild',
    reaction: '',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      setForm({
        allergy_name: allergy?.allergy_name || '',
        severity: allergy?.severity || 'Mild',
        reaction: allergy?.reaction || '',
        notes: allergy?.notes || ''
      });
    }
  }, [allergy, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{allergy ? 'Edit Allergy' : 'Add Allergy'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="allergy_name">Allergy Name *</Label>
            <Input
              id="allergy_name"
              value={form.allergy_name}
              onChange={(e) => setForm({ ...form, allergy_name: e.target.value })}
              required
              placeholder="e.g., Penicillin, Peanuts"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="severity">Severity *</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Mild">Mild</SelectItem>
                <SelectItem value="Moderate">Moderate</SelectItem>
                <SelectItem value="Severe">Severe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reaction">Reaction</Label>
            <Textarea
              id="reaction"
              value={form.reaction}
              onChange={(e) => setForm({ ...form, reaction: e.target.value })}
              placeholder="Describe the allergic reaction"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {allergy ? 'Update' : 'Add'} Allergy
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Emergency Contact Form Dialog
export const EmergencyContactFormDialog = ({ open, onOpenChange, contact, onSave, loading }) => {
  const [form, setForm] = useState({
    full_name: '',
    relationship: '',
    phone: '',
    email: '',
    address: '',
    is_primary: false,
    notes: ''
  });

  useEffect(() => {
    if (open) {
      setForm({
        full_name: contact?.full_name || '',
        relationship: contact?.relationship || '',
        phone: contact?.phone || '',
        email: contact?.email || '',
        address: contact?.address || '',
        is_primary: contact?.is_primary || false,
        notes: contact?.notes || ''
      });
    }
  }, [contact, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{contact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name *</Label>
            <Input
              id="full_name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="relationship">Relationship *</Label>
            <Select value={form.relationship || 'not_selected'} onValueChange={(v) => setForm({ ...form, relationship: v === 'not_selected' ? '' : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select relationship" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_selected">Select relationship</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="spouse">Spouse</SelectItem>
                <SelectItem value="sibling">Sibling</SelectItem>
                <SelectItem value="child">Child</SelectItem>
                <SelectItem value="guardian">Guardian</SelectItem>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="support_coordinator">Support Coordinator</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="is_primary"
              checked={form.is_primary}
              onCheckedChange={(checked) => setForm({ ...form, is_primary: checked })}
            />
            <Label htmlFor="is_primary">Primary Contact</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {contact ? 'Update' : 'Add'} Contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Medical Information Form Dialog
export const MedicalInfoFormDialog = ({ open, onOpenChange, medicalInfo, onSave, loading }) => {
  const [form, setForm] = useState({
    condition: '',
    diagnosis_date: '',
    treating_doctor: '',
    treatment_plan: '',
    notes: ''
  });

  useEffect(() => {
    if (open) {
      setForm({
        condition: medicalInfo?.condition || '',
        diagnosis_date: medicalInfo?.diagnosis_date || '',
        treating_doctor: medicalInfo?.treating_doctor || '',
        treatment_plan: medicalInfo?.treatment_plan || '',
        notes: medicalInfo?.notes || ''
      });
    }
  }, [medicalInfo, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{medicalInfo ? 'Edit Medical Information' : 'Add Medical Information'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="condition">Medical Condition *</Label>
            <Input
              id="condition"
              value={form.condition}
              onChange={(e) => setForm({ ...form, condition: e.target.value })}
              required
              placeholder="e.g., Diabetes Type 2, Epilepsy"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="diagnosis_date">Diagnosis Date</Label>
            <Input
              id="diagnosis_date"
              type="date"
              value={form.diagnosis_date}
              onChange={(e) => setForm({ ...form, diagnosis_date: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="treating_doctor">Treating Doctor</Label>
            <Input
              id="treating_doctor"
              value={form.treating_doctor}
              onChange={(e) => setForm({ ...form, treating_doctor: e.target.value })}
              placeholder="Doctor's name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="treatment_plan">Treatment Plan</Label>
            <Textarea
              id="treatment_plan"
              value={form.treatment_plan}
              onChange={(e) => setForm({ ...form, treatment_plan: e.target.value })}
              placeholder="Describe the treatment plan"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {medicalInfo ? 'Update' : 'Add'} Medical Info
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Medical Alert Form Dialog
export const MedicalAlertFormDialog = ({ open, onOpenChange, alert, onSave, loading }) => {
  const [form, setForm] = useState({
    alert_type: '',
    description: '',
    severity: 'Medium',
    instructions: '',
    requires_immediate_action: false
  });

  useEffect(() => {
    if (open) {
      setForm({
        alert_type: alert?.alert_type || '',
        description: alert?.description || '',
        severity: alert?.severity || 'Medium',
        instructions: alert?.instructions || '',
        requires_immediate_action: alert?.requires_immediate_action || false
      });
    }
  }, [alert, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{alert ? 'Edit Medical Alert' : 'Add Medical Alert'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="alert_type">Alert Type *</Label>
            <Select value={form.alert_type || 'not_selected'} onValueChange={(v) => setForm({ ...form, alert_type: v === 'not_selected' ? '' : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select alert type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_selected">Select type</SelectItem>
                <SelectItem value="allergy">Allergy Alert</SelectItem>
                <SelectItem value="medication">Medication Alert</SelectItem>
                <SelectItem value="fall_risk">Fall Risk</SelectItem>
                <SelectItem value="seizure">Seizure Risk</SelectItem>
                <SelectItem value="choking">Choking Risk</SelectItem>
                <SelectItem value="aspiration">Aspiration Risk</SelectItem>
                <SelectItem value="blood_pressure">Blood Pressure Alert</SelectItem>
                <SelectItem value="diabetes">Diabetes Alert</SelectItem>
                <SelectItem value="behavior">Behaviour Alert</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
              placeholder="Describe the alert"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="severity">Severity *</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="instructions">Response Instructions</Label>
            <Textarea
              id="instructions"
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              placeholder="What to do when this alert is triggered"
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="requires_immediate_action"
              checked={form.requires_immediate_action}
              onCheckedChange={(checked) => setForm({ ...form, requires_immediate_action: checked })}
            />
            <Label htmlFor="requires_immediate_action">Requires Immediate Action</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {alert ? 'Update' : 'Add'} Alert
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Goal Form Dialog
export const GoalFormDialog = ({ open, onOpenChange, goal, onSave, loading }) => {
  const [form, setForm] = useState({
    goal_title: '',
    goal_type: '',
    description: '',
    target_date: '',
    status: 'Not Started',
    progress_notes: ''
  });

  useEffect(() => {
    if (open) {
      setForm({
        goal_title: goal?.goal_title || '',
        goal_type: goal?.goal_type || '',
        description: goal?.description || '',
        target_date: goal?.target_date || '',
        status: goal?.status || 'Not Started',
        progress_notes: goal?.progress_notes || ''
      });
    }
  }, [goal, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{goal ? 'Edit Goal' : 'Add Goal'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="goal_title">Goal Title *</Label>
            <Input
              id="goal_title"
              value={form.goal_title}
              onChange={(e) => setForm({ ...form, goal_title: e.target.value })}
              required
              placeholder="e.g., Improve social skills"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="goal_type">Goal Type</Label>
            <Select value={form.goal_type || 'not_selected'} onValueChange={(v) => setForm({ ...form, goal_type: v === 'not_selected' ? '' : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select goal type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_selected">Select type</SelectItem>
                <SelectItem value="daily_living">Daily Living</SelectItem>
                <SelectItem value="social">Social & Community</SelectItem>
                <SelectItem value="health">Health & Wellbeing</SelectItem>
                <SelectItem value="employment">Employment</SelectItem>
                <SelectItem value="education">Education & Learning</SelectItem>
                <SelectItem value="independence">Independence</SelectItem>
                <SelectItem value="communication">Communication</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
              placeholder="Describe the goal in detail"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="target_date">Target Date</Label>
              <Input
                id="target_date"
                type="date"
                value={form.target_date}
                onChange={(e) => setForm({ ...form, target_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Achieved">Achieved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="progress_notes">Progress Notes</Label>
            <Textarea
              id="progress_notes"
              value={form.progress_notes}
              onChange={(e) => setForm({ ...form, progress_notes: e.target.value })}
              placeholder="Notes on progress towards this goal"
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {goal ? 'Update' : 'Add'} Goal
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Behaviour Support Plan Form Dialog
export const BehaviourSupportPlanFormDialog = ({ open, onOpenChange, plan, onSave, loading }) => {
  const [form, setForm] = useState({
    behaviour_type: '',
    triggers: '',
    warning_signs: '',
    prevention_strategies: '',
    response_strategies: '',
    post_incident_support: '',
    review_date: ''
  });

  useEffect(() => {
    if (open) {
      setForm({
        behaviour_type: plan?.behaviour_type || '',
        triggers: plan?.triggers || '',
        warning_signs: plan?.warning_signs || '',
        prevention_strategies: plan?.prevention_strategies || '',
        response_strategies: plan?.response_strategies || '',
        post_incident_support: plan?.post_incident_support || '',
        review_date: plan?.review_date || ''
      });
    }
  }, [plan, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{plan ? 'Edit Behaviour Support Plan' : 'Add Behaviour Support Plan'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="behaviour_type">Behaviour Type *</Label>
            <Select value={form.behaviour_type || 'not_selected'} onValueChange={(v) => setForm({ ...form, behaviour_type: v === 'not_selected' ? '' : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select behaviour type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_selected">Select type</SelectItem>
                <SelectItem value="aggression">Aggression</SelectItem>
                <SelectItem value="self_harm">Self-Harm</SelectItem>
                <SelectItem value="property_damage">Property Damage</SelectItem>
                <SelectItem value="absconding">Absconding</SelectItem>
                <SelectItem value="verbal_outburst">Verbal Outburst</SelectItem>
                <SelectItem value="non_compliance">Non-Compliance</SelectItem>
                <SelectItem value="anxiety">Anxiety/Panic</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="triggers">Known Triggers *</Label>
            <Textarea
              id="triggers"
              value={form.triggers}
              onChange={(e) => setForm({ ...form, triggers: e.target.value })}
              required
              placeholder="What situations or events typically trigger this behaviour?"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="warning_signs">Warning Signs</Label>
            <Textarea
              id="warning_signs"
              value={form.warning_signs}
              onChange={(e) => setForm({ ...form, warning_signs: e.target.value })}
              placeholder="What are the early warning signs?"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prevention_strategies">Prevention Strategies *</Label>
            <Textarea
              id="prevention_strategies"
              value={form.prevention_strategies}
              onChange={(e) => setForm({ ...form, prevention_strategies: e.target.value })}
              required
              placeholder="How can this behaviour be prevented?"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="response_strategies">Response Strategies *</Label>
            <Textarea
              id="response_strategies"
              value={form.response_strategies}
              onChange={(e) => setForm({ ...form, response_strategies: e.target.value })}
              required
              placeholder="How should staff respond when this behaviour occurs?"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="post_incident_support">Post-Incident Support</Label>
            <Textarea
              id="post_incident_support"
              value={form.post_incident_support}
              onChange={(e) => setForm({ ...form, post_incident_support: e.target.value })}
              placeholder="What support should be provided after an incident?"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="review_date">Review Date</Label>
            <Input
              id="review_date"
              type="date"
              value={form.review_date}
              onChange={(e) => setForm({ ...form, review_date: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {plan ? 'Update' : 'Add'} Plan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Archive Confirmation Dialog
export const ArchiveConfirmDialog = ({ open, onOpenChange, itemType, itemName, onConfirm, loading }) => {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    onConfirm(reason);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive {itemType}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-muted-foreground mb-4">
            Archiving removes <strong>{itemName}</strong> from active lists but keeps data accessible.
          </p>
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for archiving..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Archive {itemType}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Profile Dialog for Participants
export const EditParticipantProfileDialog = ({ open, onOpenChange, participant, houses, onSave, loading }) => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    legal_name: '',
    date_of_birth: '',
    ndis_number: '',
    phone: '',
    email: '',
    house_id: '',
    plan_start_date: '',
    plan_end_date: '',
    support_notes: '',
    photo_consent: false
  });

  useEffect(() => {
    if (participant && open) {
      setForm({
        first_name: participant.first_name || '',
        last_name: participant.last_name || '',
        preferred_name: participant.preferred_name || '',
        legal_name: participant.legal_name || '',
        date_of_birth: participant.date_of_birth || '',
        ndis_number: participant.ndis_number || '',
        phone: participant.phone || '',
        email: participant.email || '',
        house_id: participant.house_id || '',
        plan_start_date: participant.plan_start_date || '',
        plan_end_date: participant.plan_end_date || '',
        support_notes: participant.support_notes || '',
        photo_consent: participant.photo_consent || false
      });
    }
  }, [participant, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert empty house_id back to null/empty for backend
    const submitData = {
      ...form,
      house_id: form.house_id === '__unassigned__' ? '' : form.house_id
    };
    onSave(submitData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Participant Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="preferred_name">Preferred Name</Label>
              <Input
                id="preferred_name"
                value={form.preferred_name}
                onChange={(e) => setForm({ ...form, preferred_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="legal_name">Legal Name</Label>
              <Input
                id="legal_name"
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth *</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ndis_number">NDIS Number</Label>
              <Input
                id="ndis_number"
                value={form.ndis_number}
                onChange={(e) => setForm({ ...form, ndis_number: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="house_id">Assigned House</Label>
            <Select value={form.house_id || '__unassigned__'} onValueChange={(v) => setForm({ ...form, house_id: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select house" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">Unassigned (Community Client)</SelectItem>
                {houses?.map(house => (
                  <SelectItem key={house.id} value={house.id}>{house.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="plan_start_date">Plan Start Date</Label>
              <Input
                id="plan_start_date"
                type="date"
                value={form.plan_start_date}
                onChange={(e) => setForm({ ...form, plan_start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan_end_date">Plan End Date</Label>
              <Input
                id="plan_end_date"
                type="date"
                value={form.plan_end_date}
                onChange={(e) => setForm({ ...form, plan_end_date: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="support_notes">Support Notes</Label>
            <Textarea
              id="support_notes"
              value={form.support_notes}
              onChange={(e) => setForm({ ...form, support_notes: e.target.value })}
              rows={4}
            />
          </div>
          <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
            <Switch
              id="photo_consent"
              checked={form.photo_consent}
              onCheckedChange={(checked) => setForm({ ...form, photo_consent: checked })}
            />
            <div>
              <Label htmlFor="photo_consent" className="font-medium">Photography Permission</Label>
              <p className="text-sm text-muted-foreground">Allow photos to be taken and shared</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Profile Dialog for Users
export const EditUserProfileDialog = ({ open, onOpenChange, user, onSave, loading, currentUserRole }) => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'worker',
    address: '',
    is_active: true
  });

  useEffect(() => {
    if (user && open) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'worker',
        address: user.address || '',
        is_active: user.is_active !== false
      });
    }
  }, [user, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          {currentUserRole === 'admin' && (
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="house_manager">House Manager</SelectItem>
                  <SelectItem value="worker">Worker</SelectItem>
                  <SelectItem value="family">Family</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={form.is_active}
              onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
            />
            <Label htmlFor="is_active">Active Status</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
